import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction } from '@lyearn/i18n';
import { Menu2, Menu3, StarOutline } from '@lyearn/icons';
import { Field, FieldType, FormProps } from '@/components/Form';
import { renderMediumOptionLabel } from '@/components/Form/components/Select/optionLabelRenderer';
import { renderMediumSelectValue } from '@/components/Form/components/Select/renderValue';
import { FORM_PICKLIST_PROPS } from '@/modules/corporate/components/picklist';
import { fieldSettingsSectionStyles } from '@/modules/corporate/modules/database/fields/utils/editor/styles';
import QuestionSubworkspaces from '@/modules/corporate/modules/quiz/views/QuizEditor/components/QuestionEditor/components/QuestionsSettings/components/QuestionSubworkspaces';
import { QuestionEditorAccessType } from '@/modules/question/components/QuestionEditorModal/types';
import { QuestionEditorViewModes } from '@/modules/question/types';
import {
  USER_SURVEY_QUESTION_TYPES,
  USER_SURVEY_SINGLE_SELECT_OR_MULTISELECT,
} from '@/modules/user-survey/questions';
import ThemeAsyncSelect from '@/modules/user-survey/views/UserSurveyEditor/components/QuestionsView/components/ThemeAsyncSelect';
import { DEFAULT_RATING_LABELS } from '@/modules/user-survey/views/UserSurveyEditor/utils/adapters';
import {
  Maybe,
  QuestionApplicableOnEnum,
  QuestionSubTypeEnum,
  QuestionTextlengthEnum,
  QuestionTypeEnum,
  UpdateQuestionEntity,
} from '@/types/schema';

import RatingLabels from '../../../RatingLabels';

export interface GetUSurveyBankQuestionSettingsInitialValuesParams {
  question: Pick<
    UpdateQuestionEntity,
    | 'questionType'
    | 'settings'
    | 'themeIds'
    | 'subType'
    | 'customRatingComponent'
    | 'subWorkspaceIds'
  >;
}

const defaultRatingScale = 10;

export const getUSurveyBankQuestionSettingsInitialValues = (
  params: GetUSurveyBankQuestionSettingsInitialValuesParams,
) => {
  const { question } = params;

  let questionSettings: NonNullable<UpdateQuestionEntity['settings']> = {
    enableDescription: question.settings?.enableDescription ?? false,
  };

  switch (question.questionType) {
    case QuestionTypeEnum.Text: {
      questionSettings.textLength = question.settings?.textLength ?? QuestionTextlengthEnum.Short;
      break;
    }
    case QuestionTypeEnum.Rating: {
      questionSettings.ratingScale = question.settings?.ratingScale ?? defaultRatingScale;
      break;
    }
  }

  const subType =
    question.subType ??
    (question.questionType === QuestionTypeEnum.Likert
      ? QuestionSubTypeEnum.LikertRating
      : undefined);

  const customRatingComponent =
    question.questionType === QuestionTypeEnum.Likert &&
    question.subType === QuestionSubTypeEnum.CustomRating
      ? question.customRatingComponent || { ratingLabels: DEFAULT_RATING_LABELS }
      : undefined;

  return {
    questionType: question.questionType,
    settings: { ...question.settings, ...questionSettings },
    themeIds: question.themeIds,
    subType,
    customRatingComponent,
    subWorkspaceIds: { set: question.subWorkspaceIds?.set || [] },
  };
};

const getQuestionTypeOptions = (t: TFunction) =>
  [
    ...USER_SURVEY_QUESTION_TYPES,
    {
      Icon: StarOutline,
      value: QuestionTypeEnum.Rating,
      labelKey: 'UserSurvey:QuestionTypes.Rating',
    },
  ].map((item) => ({ Icon: item.Icon, label: t(item.labelKey), value: item.value }));

const getQuestionTypeOptionsSingleOrMultiselect = (t: TFunction) =>
  [...USER_SURVEY_SINGLE_SELECT_OR_MULTISELECT].map((item) => ({
    Icon: item.Icon,
    label: t(item.labelKey),
    value: item.value,
  }));

const getQuestionTypeFormField = (t: TFunction, singleOrMultiselect?: boolean): Field => {
  return {
    name: 'questionType',
    label: t('UserSurvey:QuestionSettings.QuestionType'),
    type: FieldType.Picklist,
    disabled: !singleOrMultiselect,
    picklistValues: singleOrMultiselect
      ? getQuestionTypeOptionsSingleOrMultiselect(t)
      : getQuestionTypeOptions(t),
    props: FORM_PICKLIST_PROPS,
  };
};

const getDescriptionEnabledFormField = (t: TFunction, disabled: boolean): Field => ({
  name: 'settings.enableDescription',
  label: t('Common:Form.Description'),
  type: FieldType.Switch,
  disabled,
});

const getThemeSelectorFormField = (t: TFunction, disabled: boolean): Field => ({
  name: 'themeIds.set',
  type: FieldType.Custom,
  label: t('UserSurvey:QuestionSettings.Theme'),
  Component: ({ value, onChange, error }) => (
    <ThemeAsyncSelect disabled={disabled} error={!!error} value={value} onChange={onChange} />
  ),
  disabled,
});

const getTextTypeOptions = (t: TFunction) => [
  { Icon: Menu2, label: t('Database:ShortText'), value: QuestionTextlengthEnum.Short },
  { Icon: Menu3, label: t('Database:LongText'), value: QuestionTextlengthEnum.Long },
];

const getTextLengthFormField = (t: TFunction, disabled: boolean): Field => ({
  name: 'settings.textLength',
  label: t('UserSurvey:QuestionSettings.DescriptionLength'),
  type: FieldType.Picklist,
  picklistValues: getTextTypeOptions(t),
  props: {
    className: FORM_PICKLIST_PROPS.className,
    optionLabelRenderer: renderMediumOptionLabel,
    renderValue: renderMediumSelectValue,
  },
  disabled,
});

const getRatingScaleFormField = (t: TFunction, disabled: boolean): Field => ({
  name: 'settings.ratingScale',
  label: t('UserSurvey:QuestionSettings.RatingScale'),
  type: FieldType.Number,
  disabled,
});

const getScaleTypeOptions = (t: TFunction) => [
  { label: t('UserSurvey:QuestionSettings.LikertScale'), value: QuestionSubTypeEnum.LikertRating },
  { label: t('UserSurvey:QuestionSettings.CustomScale'), value: QuestionSubTypeEnum.CustomRating },
];

const getSubTypeFormField = (t: TFunction, disabled: boolean): Field => ({
  name: 'subType',
  label: t('UserSurvey:QuestionSettings.ScaleType'),
  type: FieldType.Picklist,
  picklistValues: getScaleTypeOptions(t),
  disabled,
});

const getCustomRatingComponentFormField = (t: TFunction, disabled: boolean): Field => ({
  name: 'customRatingComponent.ratingLabels',
  label: t('UserSurvey:QuestionSettings.Labels'),
  type: FieldType.Custom,
  Component: ({ value, onChange, error }) => (
    <RatingLabels disabled={disabled} error={!!error} value={value} onChange={onChange} />
  ),
  disabled,
});

const getSubWorkspaceIdsFormFields = (t: TFunction, disabled: boolean): Field => ({
  name: 'subWorkspaceIds.set',
  type: FieldType.Custom,
  Component: ({ value, onChange }) => {
    return (
      <QuestionSubworkspaces allowPartialUpdates={disabled} value={value} onChange={onChange} />
    );
  },
});

export interface GetUSurveyBankQuestionSettingsFormPropsParams {
  t: TFunction;
  questionType?: Maybe<QuestionTypeEnum>;
  subType?: Maybe<QuestionSubTypeEnum>;
  viewMode: QuestionEditorViewModes;
  applicableOn: QuestionApplicableOnEnum;
  isQuestionSubWorkspacesEnabled: boolean;
  accessType?: QuestionEditorAccessType;
}

const QUESTIONS_WITHOUT_THEMES = [QuestionTypeEnum.Enps];

export const getUSurveyBankQuestionSettingsFormProps = (
  params: GetUSurveyBankQuestionSettingsFormPropsParams,
): Pick<FormProps, 'fields' | 'sectionLayout'> => {
  const {
    t,
    questionType,
    viewMode,
    applicableOn,
    subType,
    isQuestionSubWorkspacesEnabled,
    accessType,
  } = params;
  const disabled = viewMode === 'view';
  const isUserSurvey = applicableOn === QuestionApplicableOnEnum.UserSurvey;

  const singleOrMultiselect =
    viewMode === 'view' &&
    (questionType === QuestionTypeEnum.SingleSelect ||
      questionType === QuestionTypeEnum.MultiSelect);

  return {
    fields: {
      questionType: getQuestionTypeFormField(t, singleOrMultiselect),
      'settings.enableDescription': getDescriptionEnabledFormField(t, disabled),
      'themeIds.set': getThemeSelectorFormField(t, disabled),
      'settings.textLength': getTextLengthFormField(t, disabled),
      'settings.ratingScale': getRatingScaleFormField(t, disabled),
      subType: getSubTypeFormField(t, disabled),
      'customRatingComponent.ratingLabels': getCustomRatingComponentFormField(t, disabled),
      'subWorkspaceIds.set': getSubWorkspaceIdsFormFields(t, disabled),
    },
    sectionLayout: getEnabledItems([
      {
        id: 'type',
        className: fieldSettingsSectionStyles(32),
        layout: getEnabledItems([
          {
            name: 'questionType',
            enabled: true,
          },
          {
            name: 'themeIds.set',
            enabled: isUserSurvey && !QUESTIONS_WITHOUT_THEMES.includes(questionType!),
          },
          {
            name: 'settings.textLength',
            enabled: questionType === QuestionTypeEnum.Text,
          },
          { name: 'subType', enabled: questionType === QuestionTypeEnum.Likert },
        ]).map(({ name }) => name),
        enabled: true,
      },
      {
        id: 'labels',
        className: fieldSettingsSectionStyles(32),
        layout: ['customRatingComponent.ratingLabels'],
        enabled:
          questionType === QuestionTypeEnum.Likert && subType === QuestionSubTypeEnum.CustomRating,
      },
      {
        id: 'options',
        className: fieldSettingsSectionStyles(24),
        layout: getEnabledItems([
          {
            name: 'settings.enableDescription',
            enabled: true,
          },
          {
            name: 'settings.ratingScale',
            enabled: questionType === QuestionTypeEnum.Rating,
          },
        ]).map(({ name }) => name),
        enabled: true,
      },
      {
        id: 'sub-workspaces',
        className: fieldSettingsSectionStyles(32),
        layout: ['subWorkspaceIds.set'],
        name: t('Common:Form.Workspaces'),
        enabled: isQuestionSubWorkspacesEnabled && accessType === 'question-bank',
      },
    ]),
  };
};
